<template>
  <b-modal
    :id="modalId"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-esc
    @shown="init"
    @hidden="cancelImportCloseModal"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div
          class="loading-spinner"
          v-if="
            this.actionPlanPreviouslyRunning ||
              this.actionPlanLoading ||
              this.actionPlanRunning
          "
        >
          <div class="loader"></div>
        </div>
        <div class="loaded-content" v-else>
          <div
            class="action-plan-staging"
            v-if="
              !this.actionPlanLoading &&
                !this.actionPlanRunning &&
                Object.keys(this.actionPlanResult).length === 0
            "
          >
            <div
              class="action-plan-fetch-failure"
              v-if="
                !this.actionPlanPreviouslyRunning &&
                  !this.actionPlanLoading &&
                  !this.actionPlanRunning &&
                  Object.keys(this.actionPlan).length === 0
              "
            >
              {{ $t("errorRetrievingActionPlan") }}
            </div>
            <div class="action-plan-fetch-success" v-else>
              <div class="reports-scanned">
                {{ getNumReportsScannedLabel() }}
              </div>
              <div class="no-action" v-if="!shouldActionPlanShow()">
                {{ $t("allPagesInStudio") }}
              </div>
              <div class="yes-action" v-else>
                <ImportCustomContentModalSection
                  v-if="Object.keys(this.errorContents).length !== 0"
                  :title="getCannotImportReportsLabel()"
                  :content="this.errorContents"
                  :fields="this.errorFields"
                >
                </ImportCustomContentModalSection>

                <ImportCustomContentModalSectionEditable
                  v-if="Object.keys(this.newCustomContents).length !== 0"
                  :title="getNewCustomContentLabel()"
                  :content="this.newCustomContents"
                  :fields="this.newActionPlanFields"
                  @action-plan-field-changed="onActionPlanFieldChanged"
                >
                </ImportCustomContentModalSectionEditable>

                <ImportCustomContentModalSection
                  v-if="Object.keys(this.existingCustomContents).length !== 0"
                  :title="getAssociatedCustomContentLabel()"
                  :content="this.existingCustomContents"
                  :fields="this.existingActionPlanFields"
                >
                </ImportCustomContentModalSection>
              </div>
            </div>
          </div>
          <div
            class="action-plan-executed"
            v-if="
              !this.actionPlanRunning &&
                Object.keys(this.actionPlanResult).length !== 0
            "
          >
            <ImportCustomContentModalSection
              v-if="
                Object.keys(this.actionPlanResult.newCustomContents).length !==
                  0
              "
              :title="getExecutedNewContentTitle()"
              :content="this.actionPlanResult.newCustomContents"
              :fields="this.actionPlanNewResultFields"
            >
            </ImportCustomContentModalSection>

            <ImportCustomContentModalSection
              v-if="
                Object.keys(this.actionPlanResult.existingCustomContents)
                  .length !== 0
              "
              :title="getPreexistingContentTitle()"
              :content="this.actionPlanResult.existingCustomContents"
              :fields="this.actionPlanExistingResultFields"
            >
            </ImportCustomContentModalSection>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button
        v-if="!actionPlanRunning && Object.keys(actionPlanResult).length === 0"
        class="modal-btn modal-cancel-btn"
        @click="cancelImportCloseModal"
      >
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        v-if="
          !actionPlanRunning &&
            Object.keys(actionPlan).length !== 0 &&
            Object.keys(actionPlanResult).length === 0
        "
        class="modal-btn modal-save-btn"
        @click="submitActionPlan"
        :disabled="actionPlanLoading"
      >
        {{ getMessageText(messages.OK) }}
      </button>

      <button
        v-if="!actionPlanRunning && Object.keys(actionPlanResult).length !== 0"
        class="modal-btn modal-save-btn"
        @click="cancelImportCloseModal"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import services from "@/services";
  import ImportCustomContentModalSection from "@/components/ImportCustomContentModalSection.vue";
  import ImportCustomContentModalSectionEditable from "@/components/ImportCustomContentModalSectionEditable.vue";
  import store from "@/store";
  import { MUTATION_NAME } from "@/static/global-variables";

  export default {
    name: "ImportCustomContentModal",
    components: {
      ImportCustomContentModalSection,
      ImportCustomContentModalSectionEditable
    },
    data() {
      return {
        appName: "",
        actionPlan: {},
        actionPlanResult: {},
        newCustomContents: null,
        existingCustomContents: null,
        errorContents: null,
        translatedErrorContents: null,
        hashCodePaths: [],
        actionPlanLoading: true,
        actionPlanRunning: false,
        actionPlanPreviouslyRunning: true,
        actionPlanCancelled: false,
        errorFields: [
          { key: "reportName", label: this.$t("reportName") },
          { key: "error", label: this.$t("reason") }
        ],
        newActionPlanFields: [
          { key: "reportName", label: this.$t("reportName") },
          { key: "contentName", label: this.$t("pageName") },
          { key: "description", label: this.$t("description") }
        ],
        existingActionPlanFields: [
          { key: "reportName", label: this.$t("reportName") },
          { key: "contentName", label: this.$t("pageName") },
          { key: "description", label: this.$t("description") },
          { key: "studioVersion", label: this.$t("studioVersion") }
        ],
        actionPlanNewResultFields: [
          { key: "reportName", label: this.$t("reportName") },
          { key: "contentName", label: this.$t("pageName") },
          { key: "description", label: this.$t("description") },
          { key: "successful", label: this.$t("deploymentStatusHeader") }
        ],
        actionPlanExistingResultFields: [
          { key: "reportName", label: this.$t("reportName") },
          { key: "contentName", label: this.$t("pageName") },
          { key: "description", label: this.$t("description") },
          { key: "studioVersion", label: this.$t("studioVersion") },
          { key: "successful", label: this.$t("deploymentStatusHeader") }
        ]
      };
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages"
      }),
      modalId() {
        return "import-cc";
      },
      titleText() {
        return this.$t("importCustomPageFromCRM");
      }
    },
    methods: {
      init() {
        this.fetchActionPlan();
      },

      resetDataFields() {
        this.actionPlan = {};
        this.actionPlanResult = {};
        this.newCustomContents = null;
        this.existingCustomContents = null;
        this.errorContents = null;
        this.translatedErrorContents = null;
        this.hashCodePaths = [];
        this.actionPlanLoading = false;
        this.actionPlanRunning = false;
        this.actionPlanPreviouslyRunning = true;
        this.actionPlanCancelled = false;
      },

      checkServerActionPlanRunning() {
        services.checkActionPlanStatus().then(resp => {
          if (resp) {
            if (!resp.currentlyRunning) {
              this.actionPlanPreviouslyRunning = false;
              this.fetchActionPlan();
            } else {
              setTimeout(this.fetchActionPlan, 60000);
            }
          }
        });
      },

      fetchActionPlan() {
        if (this.actionPlanPreviouslyRunning) {
          if (this.actionPlanCancelled) {
            return;
          }
          this.checkServerActionPlanRunning();
        } else {
          this.actionPlanLoading = true;
          services.fetchActionPlan().then(resp => {
            this.actionPlan = resp;
            if (resp) {
              this.actionPlan = resp;
              this.newCustomContents = resp.newCustomContents;
              this.existingCustomContents = resp.existingCustomContents;
              this.errorContents = resp.errorContents;
              this.translatedErrorContents = this.translateErrorContents(
                this.errorContents
              );
              this.hashCodePaths = resp.hashCodePaths;
            }
            this.actionPlanLoading = false;
            if (this.actionPlanCancelled) {
              services.abandonActionPlan(this.actionPlan);
            }
          });
        }
      },

      onActionPlanFieldChanged(contentId, contentName, description) {
        this.actionPlan["newCustomContents"][contentId][
          "contentName"
        ] = contentName;
        this.actionPlan["newCustomContents"][contentId][
          "description"
        ] = description;
      },

      submitActionPlan() {
        if (!this.isActionAvailable()) {
          this.cancelImportCloseModal();
          return;
        }

        this.actionPlanRunning = true;
        services.submitActionPlan(this.actionPlan).then(resp => {
          if (resp) {
            this.actionPlanResult = resp;
            for (let key of Object.keys(resp.newCustomContents)) {
              let newApp = {
                name: resp.newCustomContents[key]["contentName"],
                type: resp.newCustomContents[key]["type"],
                customContent: true,
                description: resp.newCustomContents[key]["description"],
                isTemplate: false,
                uuid: resp.newCustomContents[key]["studioId"]
              };
              store.commit(MUTATION_NAME.ADD_APP, newApp);
            }
          }
          this.actionPlanRunning = false;
        });
      },

      getNumReportsScannedLabel() {
        let numReportsScanned =
          this.actionPlan.scannedContentCount === undefined
            ? 0
            : this.actionPlan.scannedContentCount;
        return this.$t("htmlReportsScanned").replace("{0}", numReportsScanned);
      },

      getCannotImportReportsLabel() {
        let numErrorContents = this.errorContents
          ? Object.keys(this.errorContents).length
          : 0;
        return this.$t("xHtmlReportsCannotImport").replace(
          "{0}",
          numErrorContents
        );
      },

      getNewCustomContentLabel() {
        let numNewReports = this.newCustomContents
          ? Object.keys(this.newCustomContents).length
          : 0;
        return this.$t("xNewCustomPageCreated").replace(
          "{0}",
          numNewReports
        );
      },

      getAssociatedCustomContentLabel() {
        let numExistingReports = this.existingCustomContents
          ? Object.keys(this.existingCustomContents).length
          : 0;
        return this.$t("xHtmlReportsAssociatedPages").replace(
          "{0}",
          numExistingReports
        );
      },

      shouldActionPlanShow() {
        return (
          this.isActionAvailable() || Object.keys(this.errorContents).length > 0
        );
      },

      isActionAvailable() {
        if (Object.keys(this.actionPlanResult).length > 0) {
          return false;
        }
        if (Object.keys(this.actionPlan).length > 0) {
          return (
            Object.keys(this.existingCustomContents).length > 0 ||
            Object.keys(this.newCustomContents).length > 0
          );
        }
        return false;
      },

      getExecutedNewContentTitle() {
        let numSuccess = 0;
        let numError = 0;
        for (const key of Object.keys(
          this.actionPlanResult.newCustomContents
        )) {
          if (this.actionPlanResult.newCustomContents[key]["successful"]) {
            numSuccess += 1;
          } else {
            numError += 1;
          }
        }
        return this.$t("executedCreatedCustomPage")
          .replace("{0}", numSuccess)
          .replace("{1}", numError);
      },

      getPreexistingContentTitle() {
        let numSuccess = 0;
        let numError = 0;
        for (const key of Object.keys(
          this.actionPlanResult.existingCustomContents
        )) {
          if (this.actionPlanResult.existingCustomContents[key]["successful"]) {
            numSuccess += 1;
          } else {
            numError += 1;
          }
        }
        return this.$t("executedAssociatedCustomPage")
          .replace("{0}", numSuccess)
          .replace("{1}", numError);
      },

      getErrorText(error) {
        return this.$t(error);
      },

      translateErrorContents(errors) {
        let translatedError = {};
        for (const key of Object.keys(errors)) {
          let value = errors[key];
          value["error"] = this.getErrorText(value["error"]);
          translatedError[key] = value;
        }
        return translatedError;
      },

      cancelImportCloseModal() {
        this.$bvModal.hide(this.modalId);
        this.actionPlanCancelled = true;
        if (this.isActionAvailable()) {
          services.abandonActionPlan(this.actionPlan);
        }
        this.resetDataFields();
      }
    }
  };
</script>

<style scoped>
  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: var(--BTN_BG_BLUE, #0070d2);
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: var(--BTN_BG_BLUE, #0070d2);
    border: 1px solid var(--BTN_BG_BLUE, #0070d2);
    color: var(--WHITE, #fff);
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid var(--LIGHT_GRAY, #dddbda);
    background-color: var(--WHITE, #fff);
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE, #fff);
  }

  .wizard-container {
    min-width: 1000px;
  }

  .reports-scanned {
    font-size: 1rem;
    padding-bottom: 24px;
  }

  .loading-spinner {
    display: flex;
  }
</style>
